<!--
  带操作蒙层的图片组件：统一提供 hover 蒙层与右下角放大入口。
  业务侧通过默认 slot 自行放置删除、重新生成等定制操作。
-->
<script setup lang="ts">
  import { ref, type PropType } from 'vue'
  import AppImage from '@/components/AppImage.vue'
  import type { Image } from '@/types/image'

  const props = defineProps({
    // 缩略图地址。
    src: {
      type: String,
      required: true
    },
    // 点击放大时使用的原图地址；为空时不展示默认放大镜。
    fullscreen: {
      type: String,
      default: ''
    },
    // 全屏查看器的图片序列，用于左右切换。
    allFullScreen: {
      type: Array as PropType<Image[]>,
      default: () => []
    },
    // 图片替代文本。
    alt: {
      type: String,
      default: ''
    },
    // 缩略图填充模式，默认裁切填满容器。
    objectFit: {
      type: String,
      default: 'cover'
    },
    // 是否显示右下角默认放大入口。
    showZoom: {
      type: Boolean,
      default: true
    }
  })

  const imageRef = ref<InstanceType<typeof AppImage>>()

  const openFullscreen = () => {
    imageRef.value?.openFullscreen()
  }

  defineExpose({ openFullscreen })
</script>

<template>
  <div class="image-action">
    <AppImage
      ref="imageRef"
      class="image-action-image"
      :src="props.src"
      :fullscreen="props.fullscreen"
      :all-full-screen="props.allFullScreen"
      :preview-on-click="false"
      :alt="props.alt"
      :object-fit="props.objectFit"
    />
    <div class="image-action-mask">
      <div class="image-action-content">
        <slot />
      </div>
      <button
        v-if="props.showZoom && props.fullscreen"
        class="image-action-zoom"
        type="button"
        aria-label="放大查看图片"
        @click.stop="openFullscreen"
      ></button>
    </div>
  </div>
</template>

<style lang="stylus" scoped>
  .image-action
    position relative
    width 100%
    height 100%
    overflow hidden

  .image-action-image
    position relative
    z-index 0
    display block
    width 100%
    height 100%

    :deep(.loaded-img)
      width 100%
      height 100%
      object-fit cover

  .image-action-mask
    position absolute
    inset 0
    z-index 1
    pointer-events none

    &:before
      content ''
      position absolute
      inset 0
      z-index 0
      background rgba(0, 0, 0, 0.4)
      opacity 0
      transition opacity .2s ease-in-out

  .image-action:hover .image-action-mask
    pointer-events auto

    &:before
      opacity 1

    .image-action-content,
    .image-action-zoom
      opacity 1
      visibility visible
      pointer-events auto

  .image-action-content
    position absolute
    inset 0
    z-index 1
    opacity 0
    visibility hidden
    pointer-events none

    // 业务 slot 仍可自行使用 absolute 定位，定位参考与蒙层尺寸一致。
    transition opacity .2s ease-in-out

  .image-action-zoom
    position absolute
    z-index 2
    right 0
    bottom 0
    display flex
    align-items center
    justify-content center
    width adapt(48)
    height adapt(48)
    padding 0
    border 0
    background transparent
    opacity 0
    visibility hidden
    pointer-events none
    cursor pointer

    &:before
      content ''
      position absolute
      right adapt(8)
      bottom adapt(8)
      width adapt(24)
      height adapt(24)
      border-radius adapt(4)
      background-color rgba(0, 0, 0, 0.1)
      background-image url('https://static.zaohaowu.com/image-mcp-static/zoom-in_f33ad842b8e91b1c9ee455f547b8c92d.svg')
      background-repeat no-repeat
      background-position center
      background-size adapt(16)
      backdrop-filter blur(20px)
      -webkit-backdrop-filter blur(20px)
</style>
